/**
 * Matrice fit culturel d'une campagne : un candidat par ligne, un item du
 * référentiel par colonne, le NIVEAU brut en cellule (niveau 1 = item le plus
 * important pour le candidat).
 *
 * Même patron que `PamiCampagneTable` : `DataTable` en largeurs `auto`, colonne
 * Candidat figée. Pas de pied de tableau ici — une moyenne de rangs ipsatifs ne
 * veut rien dire, chaque candidat ayant distribué les mêmes niveaux.
 */
import {
  Card,
  CardContent,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  filtreRecherche,
  type ColonneDataTable,
  type SortingState,
} from "@repo/ui";
import { dateCourte } from "@repo/utils";
import { useMemo, useState } from "react";

/** Une passation fit-culturel, telle que renvoyée par
 *  `obtenirEvaluationsFitCulturelParCampagne`. Le NIVEAU est brut : niveau 1 =
 *  item le plus important pour le candidat. */
export type FitCulturelEvalRow = {
  _id: string;
  parcoursId: string;
  nomCandidat?: string;
  prenomCandidat?: string;
  dateFin?: number;
  niveaux: { code: string; niveau: number }[];
};

type Props = {
  evaluations: FitCulturelEvalRow[];
  /**
   * Items du référentiel fit-culturel de la campagne, dans l'ordre de
   * configuration : ils donnent les colonnes ET leur libellé. Absents (aucun
   * référentiel encore configuré), les colonnes se déduisent des passations et
   * n'affichent que le code.
   */
  dimensions?: { code: string; label: string }[];
  nbNiveaux?: number;
};

/** Ordre par défaut : les passations les plus récentes d'abord. */
const TRI_INITIAL: SortingState = [{ id: "date", desc: true }];

const RECHERCHE = {
  colonne: "candidat",
  placeholder: "Nom, prénom…",
  libelle: "Rechercher un candidat",
} as const;

const BARRE_FILTRES = { recherche: RECHERCHE };

/**
 * Dégradé du niveau : le plus important (niveau 1) est le plus soutenu. Une
 * seule teinte, pas une palette rouge↔vert — un classement ipsatif n'a ni bon ni
 * mauvais pôle, seulement un ordre de priorité.
 */
function couleurNiveau(niveau: number, nbNiveaux: number): string {
  const part = (nbNiveaux - niveau) / Math.max(1, nbNiveaux - 1);
  return `color-mix(in srgb, var(--primary) ${Math.round(20 + part * 70)}%, transparent)`;
}

function nomCandidat(ligne: FitCulturelEvalRow): string {
  return `${ligne.prenomCandidat ?? ""} ${ligne.nomCandidat ?? ""}`.trim();
}

/** Niveau d'un item pour une passation, `undefined` s'il n'a pas été classé. */
function niveauDe(ligne: FitCulturelEvalRow, code: string): number | undefined {
  return ligne.niveaux.find((n) => n.code === code)?.niveau;
}

export function FitCulturelCampagneTable({
  evaluations,
  dimensions,
  nbNiveaux = 5,
}: Props) {
  // Tri interne : la table n'a pas d'URL à elle, et l'ordre par défaut
  // (dernières passations d'abord) doit survivre au tri manuel de l'utilisateur.
  const [tri, setTri] = useState<SortingState>(TRI_INITIAL);

  const items = useMemo(
    () =>
      dimensions && dimensions.length > 0
        ? dimensions
        : [
            ...new Set(evaluations.flatMap((e) => e.niveaux.map((n) => n.code))),
          ].map((code) => ({ code, label: code })),
    [dimensions, evaluations],
  );

  const colonnes = useMemo<ColonneDataTable<FitCulturelEvalRow>[]>(
    () => [
      {
        id: "candidat",
        header: "Candidat",
        accessorFn: nomCandidat,
        cell: ({ row }) => {
          const nom = nomCandidat(row.original);
          return nom ? (
            <span className="font-medium whitespace-nowrap">{nom}</span>
          ) : (
            <span className="text-muted-foreground italic">Anonyme</span>
          );
        },
        enableHiding: false,
        filterFn: filtreRecherche<FitCulturelEvalRow>((l) => [
          l.prenomCandidat,
          l.nomCandidat,
        ]),
        meta: { libelle: "Candidat", largeur: "12rem", figee: true },
      },
      {
        id: "date",
        header: "Date",
        accessorFn: (l) => l.dateFin ?? 0,
        cell: ({ row }) => (
          <span className="data whitespace-nowrap text-muted-foreground">
            {row.original.dateFin ? dateCourte(row.original.dateFin) : "—"}
          </span>
        ),
        meta: { libelle: "Date", largeur: "7rem" },
      },
      ...items.map<ColonneDataTable<FitCulturelEvalRow>>((item) => ({
        id: item.code,
        // `asChild` sur un <span> : l'en-tête triable est déjà un <button>, et
        // un bouton dans un bouton est du HTML invalide.
        header: () => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-default block max-w-24 truncate">
                {item.label}
              </span>
            </TooltipTrigger>
            <TooltipContent>{item.label}</TooltipContent>
          </Tooltip>
        ),
        // `null` plutôt qu'`undefined` pour un item non classé : TanStack range
        // les valeurs nulles en fin de tri au lieu de les mêler aux niveaux.
        accessorFn: (l) => niveauDe(l, item.code) ?? null,
        cell: ({ row }) => {
          const niveau = niveauDe(row.original, item.code);
          return niveau === undefined ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span
              className="inline-block rounded-pastille px-2 py-0.5 text-xs font-semibold tabular-nums"
              style={{ backgroundColor: couleurNiveau(niveau, nbNiveaux) }}
              // Le niveau seul est muet : 1 = prioritaire, N = secondaire, et
              // rien à l'écran ne le dit.
              title={`Niveau ${niveau} sur ${nbNiveaux} (1 = le plus important)`}
            >
              {niveau}
            </span>
          );
        },
        meta: { libelle: item.label, largeur: "5.5rem" },
      })),
    ],
    [items, nbNiveaux],
  );

  if (evaluations.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Aucune passation PC Fit terminée dans cette campagne.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <DataTable
      colonnes={colonnes}
      donnees={evaluations}
      cleColonnes="campagne-fit-culturel"
      barreFiltres={BARRE_FILTRES}
      tri={tri}
      onTriChange={setTri}
      // La comparaison entre candidats est tout l'objet de cette table : la
      // paginer la couperait en deux.
      taillePage="toutes"
      largeurColonnes="auto"
      densite="compacte"
      videFiltre={{ titre: "Aucun candidat pour cette recherche" }}
    />
  );
}
